/**
 * A WORK ORDER'S PAPERS.
 *
 * The contractor's quotation, PAN, GST and bank details, his MSME and Udyam
 * certificates, his labour licence and insurance, test and experience
 * certificates - and, once he has acknowledged the order, the SCANNED COPY OF
 * THE ORDER HE SIGNED.
 *
 * WHY THIS UPLOADS DIRECTLY AND THE GOODS-RECEIPT SCREENS QUEUE
 * -------------------------------------------------------------
 * queueAttachments() puts a file in the offline outbox and lets the sync layer
 * carry it up when a connection exists. That is exactly right for a store-man
 * photographing a challan in a shed at Khubal with one bar of signal.
 *
 * It is the wrong shape here. Every person who attaches a paper to a work order
 * - a Procurement Manager, the Head of Procurement, the Project Head - sits at
 * Head Office on a wired connection, and what they need instead is to see the
 * file appear in the list so they know the scan went in. A queued upload shows
 * nothing until the outbox drains, and the one thing worse than a slow upload
 * is one you cannot tell happened.
 *
 * So: a direct upload, and an error if it fails. The offline machinery is
 * untouched and still serves every screen that needs it.
 *
 * WHAT STOPS THE WRONG PERSON READING ONE
 * ---------------------------------------
 * Not this file. attach_read in migration 0124 answers a WO attachment only to
 * the roles the board named, and only for a site the reader can reach; the
 * bucket is private, so a path alone opens nothing. Everything here is the
 * courtesy on top of that.
 */
import { supabase } from '@/lib/supabase'
import type { PendingAttachment } from '@/components/docs/shared'

export interface WoPaper {
  id: string
  file_name: string
  kind: string
  file_path: string
  mime_type: string | null
  size_bytes: number | null
  uploaded_at: string
  /** Null on everything filed before 0138 started recording it. */
  uploaded_by?: string | null
}

const BUCKET = 'doc-attachments'

/**
 * The same path shape the sync layer writes, `<DOC_TYPE>/<id>/<file>`, so that
 * a WO paper sits beside every other document's in the bucket and a future
 * bulk export does not need to know which screen put it there.
 */
function pathFor(docType: string, docId: string, fileName: string): string {
  const safe = fileName.replace(/[^a-zA-Z0-9._-]/g, '_')
  return `${docType}/${docId}/${Date.now()}_${safe}`
}

/**
 * The three kinds of works-contract paper, and each is narrowed in the database
 * rather than here:
 *
 *   WO  a work order's quotation, certificates and signed copy  fn_may_*_wo_docs
 *   RA  a bank advice against a running account bill            fn_may_*_ra_docs
 *   VO  the scanned signed copy of a variation order            fn_may_*_vo_docs
 *
 * 0124, 0128 and 0138 respectively. Adding a fourth here without adding an arm
 * to attach_read would make it readable by every authenticated user in the
 * company, because the policy's CASE ends in `else true` — so the migration
 * comes first and this union second, never the other way round.
 */
export type PaperDocType = 'WO' | 'RA' | 'VO'

export async function uploadWoPaper(
  woId: string, kind: string, file: File, docType: PaperDocType = 'WO',
): Promise<void> {
  const path = pathFor(docType, woId, file.name)

  const { error: upErr } = await supabase.storage
    .from(BUCKET).upload(path, file, { upsert: false })
  if (upErr) throw new Error(upErr.message)

  // uploaded_by has been on the table since 0089 and nothing has ever written
  // it. It costs one call to auth to fill in, and on a signed contract document
  // "who filed this scan" is a question that gets asked — a year later, by
  // somebody who was not there.
  const { data: auth } = await supabase.auth.getUser()

  const { error } = await supabase.from('document_attachments').insert({
    doc_type: docType, doc_id: woId, kind, file_path: path,
    file_name: file.name,
    mime_type: file.type || 'application/octet-stream',
    size_bytes: file.size,
    uploaded_by: auth?.user?.id ?? null,
  })
  if (error) {
    // The row is what makes the file findable; a file with no row is litter in
    // a private bucket that nobody will ever see or be able to delete from the
    // application. Take it back out.
    await supabase.storage.from(BUCKET).remove([path])
    throw new Error(error.message)
  }
}

/** Everything collected on the builder, uploaded once the order has a number. */
export async function uploadWoPapers(
  woId: string, papers: PendingAttachment[],
): Promise<void> {
  for (const p of papers) {
    await uploadWoPaper(woId, p.kind, p.file)
  }
}

export async function listWoPapers(
  woId: string, docType: PaperDocType = 'WO',
): Promise<WoPaper[]> {
  const { data, error } = await supabase
    .from('document_attachments')
    .select('id, file_name, kind, file_path, mime_type, size_bytes, uploaded_at, uploaded_by')
    .eq('doc_type', docType).eq('doc_id', woId)
    .order('uploaded_at')
  // A read refusal here is the policy doing its job for somebody who may not
  // see this order's papers. Show an empty list rather than a red error about
  // files they are not supposed to know exist.
  return error ? [] : ((data ?? []) as WoPaper[])
}

/** A link the browser will DISPLAY. Minted on the click; dies in an hour. */
export async function woPaperViewUrl(path: string): Promise<string> {
  const { data, error } = await supabase.storage.from(BUCKET).createSignedUrl(path, 3600)
  if (error || !data) throw new Error(error?.message ?? 'no link')
  return data.signedUrl
}

export { BUCKET as WO_PAPER_BUCKET }
